import isEmpty from "lodash/isEmpty";
import { Types } from "@grandvisionhq/graphql";
import { isNil } from "@grandvisionhq/utils";
import { convertToGqlDateFormat } from "./convertToGqlDateFormat.js";
import { isPickupPoint, isDelivery, isDeliveryExpress, isNearestPickup, isPickupStore } from "./shipping-method.js";
const createAddress = (data) => {
  const {
    additionalAddressInfo,
    additionalStreetInfo,
    city = "",
    company,
    country: formCountry,
    email,
    externalId,
    firstName = "",
    lastName = "",
    middleName,
    opcoCountry,
    phone,
    postalCode,
    region,
    salutation,
    streetName = "",
    streetNumber,
    title
  } = data;
  return {
    ...!isEmpty(additionalAddressInfo) ? { additionalAddressInfo } : null,
    ...!isEmpty(additionalStreetInfo) ? { additionalStreetInfo } : null,
    city,
    company,
    countryCode: (formCountry || opcoCountry) ?? "",
    email,
    externalId,
    firstName,
    lastName,
    middleName,
    phone,
    postalCode,
    region,
    salutation,
    streetName,
    streetNumber,
    title
  };
};
const convertPickupPointToAddress = (pickupPoint) => {
  const {
    additionalStreetInfo,
    globalPickupPointId,
    name,
    postalCode,
    streetName,
    streetNumber,
    town
  } = pickupPoint;
  return {
    additionalStreetInfo: additionalStreetInfo ?? "",
    city: town,
    company: name,
    externalId: globalPickupPointId,
    postalCode: postalCode ?? void 0,
    streetName,
    streetNumber: streetNumber ?? void 0
  };
};
var InvoiceFieldMapForPayload = /* @__PURE__ */ ((InvoiceFieldMapForPayload2) => {
  InvoiceFieldMapForPayload2["secondaryEmail"] = "certifiedEmail";
  InvoiceFieldMapForPayload2["customerCode"] = "customerCode";
  InvoiceFieldMapForPayload2["fiscalNumber"] = "customerCode";
  InvoiceFieldMapForPayload2["recipientCode"] = "certifiedRecipientCode";
  InvoiceFieldMapForPayload2["customerVatId"] = "vatId";
  InvoiceFieldMapForPayload2["insuranceProvider"] = "insuranceProvider";
  InvoiceFieldMapForPayload2["insuranceNumber"] = "insuranceNumber";
  return InvoiceFieldMapForPayload2;
})(InvoiceFieldMapForPayload || {});
const invoiceResetData = {
  certifiedEmail: null,
  vatId: null,
  certifiedRecipientCode: "",
  customerCode: "",
  insuranceNumber: null,
  insuranceProvider: ""
};
const createInvoice = (invoiceDetails) => {
  if (!invoiceDetails) {
    return true;
  }
  const invoice = Object.entries(invoiceDetails).reduce((result, [key, value]) => {
    return InvoiceFieldMapForPayload[key] ? Object.assign(result, {
      ...result,
      [InvoiceFieldMapForPayload[key]]: value
    }) : result;
  }, {});
  return invoice;
};
const getCustomerDetailsPayload = ({
  shippingDetails,
  opcoCountry,
  locale,
  isMultilingualEnabled,
  selectedPickupPoint,
  selectedShippingMethod,
  selectedStore,
  cartShippingAddress
}) => {
  const customerDetailsPayload = {
    ...isMultilingualEnabled && locale ? { locale } : null
  };
  const { billingAddress, deliveryAddress, contactDetails, invoice, company } = shippingDetails;
  let phone;
  if (contactDetails) {
    phone = contactDetails.phone ? contactDetails.phone : null;
    let dateOfBirth = contactDetails.dateOfBirth || contactDetails.customerDateOfBirth || contactDetails.customerDOB || "";
    dateOfBirth = dateOfBirth === "" ? null : dateOfBirth;
    customerDetailsPayload.customerDetails = {
      email: contactDetails.email,
      ...phone ? { phone } : null,
      ...contactDetails.secretIBAN ? { IBAN: contactDetails.secretIBAN } : null,
      ...dateOfBirth ? { dateOfBirth: convertToGqlDateFormat(dateOfBirth) } : null,
      ...isPickupPoint(selectedShippingMethod) && shippingDetails.billingAddress?.dhlPostalNumber ? { dhlPostalNumber: Number(shippingDetails.billingAddress.dhlPostalNumber) } : null,
      customerType: company ? Types.CheckoutCustomerType.B2B : Types.CheckoutCustomerType.B2C
    };
  }
  const invoiceDetails = invoice && createInvoice(invoice);
  if (invoiceDetails) {
    customerDetailsPayload.invoiceDetails = invoiceDetails;
  }
  if (contactDetails?.customerVatId) {
    customerDetailsPayload.invoiceDetails = createInvoice({
      ...invoice ? invoice : {},
      // technically not needed atm, but added to make it more resilient in case PT want to have other invoice fields too
      customerVatId: contactDetails?.customerVatId
    });
  }
  customerDetailsPayload.billingAddress = createAddress({
    opcoCountry,
    ...billingAddress
  });
  customerDetailsPayload.shippingAddress = deliveryAddress ? createAddress({
    opcoCountry,
    ...deliveryAddress
  }) : customerDetailsPayload.billingAddress;
  if (company) {
    customerDetailsPayload.invoiceDetails = createInvoice(company);
    customerDetailsPayload.billingAddress.company = company.name;
    customerDetailsPayload.shippingAddress.company = company.name;
  }
  if (customerDetailsPayload.invoiceDetails && customerDetailsPayload.invoiceDetails.certifiedEmail === "") {
    customerDetailsPayload.invoiceDetails.certifiedEmail = null;
  }
  if ((isDelivery(selectedShippingMethod) || isDeliveryExpress(selectedShippingMethod)) && phone) {
    customerDetailsPayload.shippingAddress.phone = phone;
  }
  if (isNearestPickup(selectedShippingMethod)) {
    customerDetailsPayload.shippingAddress = {
      ...customerDetailsPayload.billingAddress,
      ...phone ? { phone } : null
    };
  }
  if (isPickupStore(selectedShippingMethod)) {
    if (selectedStore) {
      customerDetailsPayload.shippingAddress = {
        city: customerDetailsPayload.billingAddress.city,
        streetName: customerDetailsPayload.billingAddress.streetName,
        ...cartShippingAddress ?? {},
        countryCode: opcoCountry,
        firstName: customerDetailsPayload.billingAddress.firstName,
        lastName: customerDetailsPayload.billingAddress.lastName,
        ...customerDetailsPayload.billingAddress.salutation ? { salutation: customerDetailsPayload.billingAddress.salutation } : {}
      };
    }
  }
  if (isPickupPoint(selectedShippingMethod)) {
    if (selectedPickupPoint) {
      const pickupPointAddress = convertPickupPointToAddress(selectedPickupPoint);
      customerDetailsPayload.shippingAddress = {
        city: customerDetailsPayload.billingAddress.city,
        streetName: customerDetailsPayload.billingAddress.streetName,
        ...pickupPointAddress,
        countryCode: opcoCountry,
        firstName: customerDetailsPayload.billingAddress.firstName,
        lastName: customerDetailsPayload.billingAddress.lastName,
        ...customerDetailsPayload.billingAddress.salutation ? { salutation: customerDetailsPayload.billingAddress.salutation } : {}
      };
    }
  }
  if (phone) {
    customerDetailsPayload.billingAddress.phone = phone;
  }
  if (isNil(customerDetailsPayload.invoiceDetails)) {
    customerDetailsPayload.invoiceDetails = invoiceResetData;
  } else {
    customerDetailsPayload.invoiceDetails = {
      ...invoiceResetData,
      ...customerDetailsPayload.invoiceDetails
    };
  }
  return customerDetailsPayload;
};
export {
  InvoiceFieldMapForPayload,
  convertPickupPointToAddress,
  createAddress,
  getCustomerDetailsPayload
};
//# sourceMappingURL=get-customer-details-payload.js.map
